import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Autocomplete,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearCustodiosPPE = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    emcodemp: "",
    cusnombre: "",
    cusdirec: "",
    custipo: "E", // E = Empleado por defecto
    cusstatus: "A",
  })

  // --- LÓGICA DEFENSIVA APLICADA (Basada en SubTiposPPE)[cite: 17] ---
  const { data: rawEmpleados, isLoading: isLoadingEmpleados } = useQuery({
    queryKey: ["listaEmpleadosPPE_Crear"],
    queryFn: async () => {
      try {
        const response = await api.get("/CustodiosPPE/getListaEmpleados")
        // Extraemos la data con la misma jerarquía defensiva[cite: 17]
        const dataExtraida = response?.data?.data || response?.data || response || []
        return dataExtraida
      } catch (error) {
        console.error("Error al cargar Empleados:", error)
        return []
      }
    },
    enabled: formData.custipo === "E", // Solo consulta si el tipo es Empleado
  })

  // Protección estricta: garantizamos que sea un Array[cite: 17]
  const empleadosValidados = Array.isArray(rawEmpleados)
    ? rawEmpleados
    : Array.isArray(rawEmpleados?.data)
      ? rawEmpleados.data
      : []

  // Mapeamos al formato exacto {id, label} que exige el Autocomplete[cite: 17]
  const listaEmpleados = empleadosValidados.map((item) => ({
    id: item.emcodemp || item.id,
    label: item.label || `${item.emcodemp} - ${item.cusnombre || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const { mutateAsync: SaveCreacionCustodio, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingCustodio"],
    fn: async (data) => {
      const response = await api.post("/CustodiosPPE/createCustodiosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value

    if (field === "custipo") {
      setFormData((prev) => ({ ...prev, custipo: value, emcodemp: "", cusnombre: "" }))
      return
    }

    // Normalización y recortes de seguridad
    if (typeof value === "string") finalValue = value.toUpperCase()
    if (field === "emcodemp") finalValue = finalValue.slice(0, 10)
    if (field === "cusnombre") finalValue = finalValue.slice(0, 100)
    if (field === "cusdirec") finalValue = finalValue.slice(0, 120)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    if (!formData.emcodemp.trim()) return showWarning("El Código del Custodio es obligatorio")
    if (!formData.cusnombre.trim()) return showWarning("El Nombre del Custodio es obligatorio")

    await SaveCreacionCustodio(formData)
  }

  // Lógica de barra de acciones dinámica[cite: 17]
  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GUARDAR" || action?.acccaption === "EJECUTAR" || action?.acccaption === "CREAR",
  )

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving || (formData.custipo === "E" && isLoadingEmpleados)}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nuevo Custodio de PPE</b>
        </div>

        <CustomBackdrop isLoading={isSaving || (formData.custipo === "E" && isLoadingEmpleados)} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Grid container spacing={3}>
              {/* SELECCIÓN DE TIPO DE CUSTODIO */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: "bold", color: "primary.main" }}>
                    Tipo de Custodio *
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.custipo}
                    onChange={(e) => handleInputChange("custipo", e.target.value)}
                  >
                    <FormControlLabel value="E" control={<Radio />} label="Empleado" />
                    <FormControlLabel value="O" control={<Radio />} label="Otros" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* AUTOCOMPLETE DEFENSIVO[cite: 17] */}
              <Grid item xs={12} sm={4}>
                {formData.custipo === "E" ? (
                  <Autocomplete
                    options={listaEmpleados}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      listaEmpleados.find((emp) => emp.id === formData.emcodemp) ||
                      (formData.emcodemp ? { id: formData.emcodemp, label: `${formData.emcodemp}` } : null)
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        // Extraemos el nombre del label para autocompletar el campo cusnombre
                        const nombreExtraido = newValue.label.includes(" - ")
                          ? newValue.label.split(" - ")[1]
                          : newValue.label
                        setFormData((prev) => ({ ...prev, emcodemp: newValue.id, cusnombre: nombreExtraido }))
                      } else {
                        setFormData((prev) => ({ ...prev, emcodemp: "", cusnombre: "" }))
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccionar Empleado *"
                        placeholder="Buscar..."
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Código Custodio (Otros) *"
                    value={formData.emcodemp}
                    onChange={(e) => handleInputChange("emcodemp", e.target.value)}
                    inputProps={{ maxLength: 10 }}
                  />
                )}
              </Grid>

              {/* NOMBRE DEL CUSTODIO */}
              <Grid item xs={12} sm={8}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Nombre Completo *"
                  value={formData.cusnombre}
                  onChange={(e) => handleInputChange("cusnombre", e.target.value)}
                  disabled={formData.custipo === "E"} // Bloqueado si es empleado
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={9}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Dirección"
                  value={formData.cusdirec}
                  onChange={(e) => handleInputChange("cusdirec", e.target.value)}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.cusstatus}
                  onChange={(e) => handleInputChange("cusstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearCustodiosPPE
